/**
 * Brouillon d'édition d'une recette — état de formulaire, sans React.
 *
 * Même partage des rôles que `templateDraft.ts` et `foodDraft.ts` : un module
 * qui exporte des composants ne doit rien exporter d'autre (`react-refresh`), et
 * ces conversions se relisent mieux séparées du rendu.
 *
 * 🔴 **Aucune macro n'entre ni ne sort d'ici.** Une recette n'en porte pas : ses
 * macros sont calculées par le serveur depuis les ingrédients, à chaque lecture
 * (specs §6.5). Le brouillon ne connaît donc que des identifiants d'aliments et
 * des quantités — il n'y a rien à saisir, rien à envoyer, rien à contredire.
 * L'aperçu affiché pendant la saisie vit dans `lib/recipePreview.ts`, et il est
 * marqué comme tel.
 */

import type { FoodDTO, RecipeDTO, RecipeInput, UpdateRecipeBody } from '../../../../shared/types';
import { newClientId } from '../../lib/dates';
import { toField, toNumber } from '../../lib/formFields';

/**
 * Un ingrédient en cours de saisie.
 *
 * ⚠️ Porte la **fiche d'aliment entière** et pas seulement son `id` : l'aperçu
 * des macros et l'affichage de la ligne (unité, portions disponibles) en ont
 * besoin à chaque frappe, et la recharger à chaque rendu coûterait une requête
 * par ingrédient.
 */
export interface IngredientDraft {
  /** Clé de rendu locale — jamais envoyée au serveur, jamais persistée. */
  key: string;
  food: FoodDTO;
  /** En unité de base sans portion, en nombre de portions avec. */
  quantity: string;
  /** `null` = saisie en unité de base (g/ml). */
  portionId: number | null;
}

export interface RecipeDraft {
  name: string;
  /** Une étape par ligne — c'est la forme que `recipe.steps` stocke (§4.3). */
  steps: string;
  /** Tags déjà posés. La saisie en cours vit dans le composant. */
  tags: string[];
  yieldType: 'portions' | 'weight';
  yieldValue: string;
  isFavorite: boolean;
  ingredients: IngredientDraft[];
}

export function emptyRecipeDraft(): RecipeDraft {
  return {
    name: '',
    steps: '',
    tags: [],
    // Le rendement en portions est le cas courant d'un plat maison ; le poids
    // total sert surtout aux préparations qu'on pèse (pâte, sauce, granola).
    yieldType: 'portions',
    yieldValue: '4',
    isFavorite: false,
    ingredients: [],
  };
}

/**
 * Brouillon depuis une recette existante.
 *
 * ⚠️ Les fiches d'aliments sont reconstituées depuis le DTO — `RecipeIngredientDTO`
 * ne porte pas la fiche entière. Les champs manquants (portions, code-barres) sont
 * complétés par `foods`, la liste déjà chargée par l'éditeur ; un ingrédient dont
 * la fiche n'est pas dans cette liste garde une fiche minimale, suffisante pour
 * l'afficher et le renvoyer tel quel.
 */
export function draftOfRecipe(recipe: RecipeDTO, foods: readonly FoodDTO[]): RecipeDraft {
  const byId = new Map(foods.map((food) => [food.id, food]));

  return {
    name: recipe.name,
    steps: recipe.steps ?? '',
    tags: [...recipe.tags],
    yieldType: recipe.yieldType,
    yieldValue: toField(recipe.yieldValue),
    isFavorite: recipe.isFavorite,
    ingredients: recipe.ingredients.map((item) => ({
      key: newClientId(),
      food: byId.get(item.foodId) ?? placeholderFood(item.foodId, item.label, item.baseUnit),
      quantity: toField(item.quantity),
      portionId: item.portionId,
    })),
  };
}

/**
 * Fiche minimale pour un aliment absent de la liste chargée.
 *
 * ⚠️ Ses `per100` sont à zéro : l'aperçu local sous-estimerait donc la recette.
 * C'est sans conséquence — le serveur, lui, lit la vraie fiche, et sa réponse
 * remplace l'aperçu à l'enregistrement. Le cas ne se présente que si la liste
 * d'aliments est tronquée, ce qu'elle n'est pas en pratique.
 */
function placeholderFood(id: number, label: string, baseUnit: 'g' | 'ml'): FoodDTO {
  return {
    id,
    name: label,
    brand: null,
    barcode: null,
    source: 'perso',
    baseUnit,
    per100: { protein: 0, carbs: 0, fat: 0, kcal: 0 },
    isFavorite: false,
    offCode: null,
    portions: [],
    createdAt: 0,
    updatedAt: 0,
  };
}

/** Ingrédients retenus : ceux dont la quantité tient debout. */
function ingredientsOf(draft: RecipeDraft) {
  return draft.ingredients
    .filter((item) => toNumber(item.quantity) > 0)
    .map((item) => ({
      foodId: item.food.id,
      quantity: toNumber(item.quantity),
      portionId: item.portionId,
    }));
}

/**
 * Le formulaire est-il envoyable ?
 *
 * Un nom, un rendement strictement positif, et **au moins un ingrédient** : une
 * recette sans ingrédient n'aurait aucune macro à calculer, ce qui la rendrait
 * inutilisable au journal (le serveur la refuserait avec `recipe_empty`).
 */
export function isRecipeDraftValid(draft: RecipeDraft): boolean {
  return (
    draft.name.trim() !== '' &&
    toNumber(draft.yieldValue) > 0 &&
    ingredientsOf(draft).length > 0
  );
}

export function inputOfRecipeDraft(draft: RecipeDraft, clientId?: string): RecipeInput {
  const steps = draft.steps.trim();
  return {
    name: draft.name.trim(),
    steps: steps === '' ? null : steps,
    tags: draft.tags,
    yieldType: draft.yieldType,
    yieldValue: toNumber(draft.yieldValue),
    isFavorite: draft.isFavorite,
    ingredients: ingredientsOf(draft),
    ...(clientId === undefined ? {} : { clientId }),
  };
}

/** Corps de `PATCH` — mêmes champs, sans `clientId` (la ressource est désignée). */
export function updateOfRecipeDraft(draft: RecipeDraft): UpdateRecipeBody {
  const steps = draft.steps.trim();
  return {
    name: draft.name.trim(),
    steps: steps === '' ? null : steps,
    tags: draft.tags,
    yieldType: draft.yieldType,
    yieldValue: toNumber(draft.yieldValue),
    isFavorite: draft.isFavorite,
    ingredients: ingredientsOf(draft),
  };
}
